import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { alsDauer, api } from '../../api/client'
import type { Auswertungszeile, Erzeugungsergebnis, Monatsplan, Tag, Zelle } from './typen'

const MONATSNAMEN = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
]

type Sicht = 'fahrer' | 'kurs' | 'auswertung'

/** Kürzel der Abwesenheitsarten, wie sie im Aushang des Betriebs stehen. */
const ABWESEND: Record<string, string> = {
  URLAUB: 'U',
  KRANKHEIT: 'AU',
  SCHULUNG: 'S',
  FREIZEITAUSGLEICH: 'FA',
}

function tagesfarbe(tag: Tag): string | undefined {
  if (tag.feiertag) return 'var(--rot-50, #fdf2f2)'
  if (tag.wochenende) return 'var(--grau-50, #f7f7f8)'
  return undefined
}

/** Der Monatsdienstplan in drei Blickrichtungen: Fahrer, Kurse, Auswertung. */
export function Dienstplan() {
  const heute = new Date()
  // Verweise aus den Klärungen öffnen einen bestimmten Monat und Kurs:
  // /dienstplan?monat=2026-09&kurs=142
  const [parameter] = useSearchParams()
  const vorgabeMonat = parameter.get('monat')?.match(/^(\d{4})-(\d{2})$/)
  const hervorheben = parameter.get('kurs')
  const [jahr, setJahr] = useState(vorgabeMonat ? Number(vorgabeMonat[1]) : heute.getFullYear())
  const [monat, setMonat] = useState(vorgabeMonat ? Number(vorgabeMonat[2]) : heute.getMonth() + 1)
  const [sicht, setSicht] = useState<Sicht>(hervorheben ? 'kurs' : 'fahrer')
  const [plan, setPlan] = useState<Monatsplan | null>(null)
  const [laedt, setLaedt] = useState(true)
  const [ergebnis, setErgebnis] = useState<Erzeugungsergebnis | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)

  const laden = useCallback(() => {
    setLaedt(true)
    api
      .get<Monatsplan>(`/api/dienstplan/monat?jahr=${jahr}&monat=${monat}`)
      .then(setPlan)
      .finally(() => setLaedt(false))
  }, [jahr, monat])

  useEffect(() => {
    laden()
    setErgebnis(null)
    setMeldung(null)
  }, [laden])

  function blaettern(richtung: number) {
    const d = new Date(jahr, monat - 1 + richtung, 1)
    setJahr(d.getFullYear())
    setMonat(d.getMonth() + 1)
  }

  async function erzeugen(vorschau: boolean) {
    setMeldung(null)
    const antwort = await api.post<Erzeugungsergebnis>(
      `/api/dienstplan/erzeugen?jahr=${jahr}&monat=${monat}&vorschau=${vorschau}`,
    )
    setErgebnis(antwort)
    if (!vorschau) {
      setMeldung(`${antwort.angelegt} Zuteilungen angelegt.`)
      laden()
    }
  }

  return (
    <div className="karte abschnitt">
      <h2>
        Dienstplan {MONATSNAMEN[monat - 1]} {jahr}
        <span
          style={{
            fontWeight: 400,
            color: 'var(--grau-500)',
            marginLeft: 10,
            fontSize: '0.88rem',
          }}
        >
          aus dem Turnus der Dienstgruppen
        </span>
      </h2>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0' }}>
        <button type="button" onClick={() => blaettern(-1)}>
          ‹ Vormonat
        </button>
        <button type="button" onClick={() => blaettern(1)}>
          Folgemonat ›
        </button>

        <span style={{ flex: 1 }} />

        <button
          type="button"
          className={sicht === 'fahrer' ? 'aktiv' : undefined}
          onClick={() => setSicht('fahrer')}
        >
          Fahrersicht
        </button>
        <button
          type="button"
          className={sicht === 'kurs' ? 'aktiv' : undefined}
          onClick={() => setSicht('kurs')}
        >
          Kurssicht
        </button>
        <button
          type="button"
          className={sicht === 'auswertung' ? 'aktiv' : undefined}
          onClick={() => setSicht('auswertung')}
        >
          Auswertung
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <button type="button" onClick={() => erzeugen(true)}>
          Erzeugung vorschauen
        </button>
        <button type="button" onClick={() => erzeugen(false)}>
          Fehlende Dienste erzeugen
        </button>
        {meldung && <span style={{ color: 'var(--gruen-600, green)' }}>{meldung}</span>}
      </div>

      {ergebnis && <Erzeugungsbericht ergebnis={ergebnis} />}

      {laedt || !plan ? (
        <div className="laden">Wird geladen …</div>
      ) : sicht === 'fahrer' ? (
        <Fahrersicht plan={plan} />
      ) : sicht === 'kurs' ? (
        <Kurssicht plan={plan} hervorheben={hervorheben} />
      ) : (
        <Auswertung zeilen={plan.auswertung} />
      )}
    </div>
  )
}

function Erzeugungsbericht({ ergebnis }: { ergebnis: Erzeugungsergebnis }) {
  return (
    <div
      className="hinweis"
      style={{
        border: '1px solid var(--grau-200, #e3e3e6)',
        borderRadius: 6,
        padding: '10px 12px',
        marginBottom: 12,
        fontSize: '0.88rem',
      }}
    >
      <strong>{ergebnis.angelegt}</strong> Dienste erzeugbar ·{' '}
      <strong>{ergebnis.uebersprungenVorhanden}</strong> bereits vorhanden ·{' '}
      <strong>{ergebnis.freieTage}</strong> freie Tage laut Turnus ·{' '}
      <strong>{ergebnis.fahrerOhneTurnus}</strong> Fahrer ohne Turnus
      {ergebnis.beanstandungen.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary>{ergebnis.beanstandungen.length} Beanstandungen</summary>
          <ul style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
            {ergebnis.beanstandungen.slice(0, 50).map((b, i) => (
              <li key={i}>
                {b.tag} · {b.fahrer} · Kurs {b.kursnummer}: {b.meldung}
              </li>
            ))}
          </ul>
          {ergebnis.beanstandungen.length > 50 && (
            <div style={{ color: 'var(--grau-500)', marginTop: 4 }}>
              … weitere {ergebnis.beanstandungen.length - 50} nicht angezeigt.
            </div>
          )}
        </details>
      )}
    </div>
  )
}

function Kopfzeile({ tage }: { tage: Tag[] }) {
  return (
    <tr>
      <th style={{ position: 'sticky', left: 0, background: 'var(--weiss, #fff)' }}>
        Mitarbeiter
      </th>
      {tage.map((t) => (
        <th
          key={t.datum}
          title={t.feiertag ?? undefined}
          style={{ textAlign: 'center', width: 34, background: tagesfarbe(t) }}
        >
          <div style={{ fontSize: '0.68rem', fontWeight: 400, color: 'var(--grau-500)' }}>
            {t.wochentag}
          </div>
          {Number(t.datum.slice(8, 10))}
        </th>
      ))}
      <th style={{ textAlign: 'right' }}>Summe</th>
    </tr>
  )
}

function Fahrersicht({ plan }: { plan: Monatsplan }) {
  return (
    <div className="tabelle-huelle" style={{ boxShadow: 'none', overflowX: 'auto' }}>
      <table>
        <thead>
          <Kopfzeile tage={plan.tage} />
        </thead>
        <tbody>
          {plan.fahrerzeilen.map((z) => (
            <tr key={z.fahrerId}>
              <td style={{ position: 'sticky', left: 0, background: 'var(--weiss, #fff)' }}>
                <strong>{z.fahrerName}</strong>
                <div style={{ fontSize: '0.72rem', color: 'var(--grau-500)' }}>
                  {z.teamName ?? '—'}
                  {z.turnusposition !== null && ` · Zeile ${z.turnusposition}`}
                </div>
              </td>
              {plan.tage.map((t) => (
                <td
                  key={t.datum}
                  style={{
                    textAlign: 'center',
                    padding: '4px 2px',
                    fontSize: '0.78rem',
                    background: tagesfarbe(t),
                  }}
                >
                  <Tageszelle zelle={z.jeTag[t.datum]} />
                </td>
              ))}
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {alsDauer(z.arbeitszeitGesamt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Tageszelle({ zelle }: { zelle: Zelle | undefined }) {
  if (!zelle) return <span style={{ color: 'var(--grau-300)' }}>·</span>
  if (zelle.art === 'DIENST') {
    return <span title={alsDauer(zelle.arbeitszeit)}>{zelle.kursnummer}</span>
  }
  if (zelle.art === 'FREI') {
    return <span style={{ color: 'var(--grau-400, #b5b5bb)' }}>F</span>
  }
  return (
    <span style={{ color: 'var(--blau-600, #2563eb)' }} title={zelle.art}>
      {ABWESEND[zelle.art] ?? zelle.art.slice(0, 2)}
    </span>
  )
}

function Kurssicht({ plan, hervorheben }: { plan: Monatsplan; hervorheben?: string | null }) {
  const markiert = useRef<HTMLTableRowElement>(null)
  useEffect(() => {
    markiert.current?.scrollIntoView({ block: 'center' })
  }, [hervorheben, plan])
  return (
    <div className="tabelle-huelle" style={{ boxShadow: 'none', overflowX: 'auto' }}>
      <table>
        <thead>
          <tr>
            <th style={{ position: 'sticky', left: 0, background: 'var(--weiss, #fff)' }}>
              Kurs
            </th>
            {plan.tage.map((t) => (
              <th
                key={t.datum}
                title={t.feiertag ?? undefined}
                style={{ textAlign: 'center', width: 34, background: tagesfarbe(t) }}
              >
                <div
                  style={{ fontSize: '0.68rem', fontWeight: 400, color: 'var(--grau-500)' }}
                >
                  {t.wochentag}
                </div>
                {Number(t.datum.slice(8, 10))}
              </th>
            ))}
            <th style={{ textAlign: 'right' }}>offen</th>
          </tr>
        </thead>
        <tbody>
          {plan.kurszeilen.map((z) => {
            const offen = z.tageZuFahren - z.tageBesetzt
            return (
              <tr
                key={z.kursnummer}
                ref={z.kursnummer === hervorheben ? markiert : undefined}
                style={z.kursnummer === hervorheben ? { outline: '2px solid var(--orange-500, #d97706)' } : undefined}
              >
                <td style={{ position: 'sticky', left: 0, background: 'var(--weiss, #fff)' }}>
                  <strong>{z.kursnummer}</strong>
                </td>
                {plan.tage.map((t) => {
                  const fahrer = z.fahrerJeTag[t.datum]
                  const zuFahren = t.datum in z.fahrerJeTag
                  return (
                    <td
                      key={t.datum}
                      title={fahrer ?? undefined}
                      style={{
                        textAlign: 'center',
                        padding: '4px 2px',
                        fontSize: '0.72rem',
                        background: tagesfarbe(t),
                      }}
                    >
                      {!zuFahren ? (
                        <span style={{ color: 'var(--grau-200, #e3e3e6)' }}>·</span>
                      ) : fahrer ? (
                        <span>{kuerzel(fahrer)}</span>
                      ) : (
                        <span style={{ color: 'var(--rot-600, #dc2626)' }} title="nicht besetzt">
                          ●
                        </span>
                      )}
                    </td>
                  )
                })}
                <td
                  style={{
                    textAlign: 'right',
                    color: offen > 0 ? 'var(--rot-600, #dc2626)' : undefined,
                  }}
                >
                  {offen}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Vorzeichenbehaftete Dauer.
 *
 * `alsDauer` rechnet mit Math.floor und Restwert und liefert fuer -90 Minuten "-2:-30 h".
 * Der Zeitsaldo ist die einzige Stelle, an der negative Werte vorkommen — deshalb hier
 * und nicht im gemeinsamen Formatierer.
 */
function alsSaldo(minuten: number): string {
  const vorzeichen = minuten < 0 ? '−' : '+'
  const betrag = Math.abs(minuten)
  return `${vorzeichen}${Math.floor(betrag / 60)}:${String(betrag % 60).padStart(2, '0')} h`
}

/** "Nachname, Vorname" auf die ersten Buchstaben des Nachnamens eindampfen. */
function kuerzel(name: string): string {
  return name.split(',')[0].trim().slice(0, 3)
}

function Auswertung({ zeilen }: { zeilen: Auswertungszeile[] }) {
  return (
    <>
      <p style={{ fontSize: '0.85rem', color: 'var(--grau-500)', marginTop: 0 }}>
        Spaltenschnitt nach dem Blatt „Auswertung" der Vorlage. Alle Werte sind Zeiten und
        Stückzahlen, keine Beträge — welcher Zuschlag auf welche Grundlage fällt, steht im
        Tarifvertrag und ist noch offen.
      </p>
      <div className="tabelle-huelle" style={{ boxShadow: 'none', overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Mitarbeiter</th>
              <th>PNr</th>
              <th>Gruppe</th>
              <th style={{ textAlign: 'right' }} title="Arbeitszeit">
                AZ
              </th>
              <th style={{ textAlign: 'right' }} title="geteilte Dienste">
                GD
              </th>
              <th style={{ textAlign: 'right' }} title="Zeit im Nachtfenster">
                NZ
              </th>
              <th style={{ textAlign: 'right' }} title="Zeit an Sonntagen">
                SZ
              </th>
              <th style={{ textAlign: 'right' }} title="Zeit an Feiertagen">
                FZ
              </th>
              <th style={{ textAlign: 'right' }} title="Krankheitstage">
                AU
              </th>
              <th style={{ textAlign: 'right' }} title="Urlaubstage">
                U
              </th>
              <th style={{ textAlign: 'right' }} title="Urlaub in Stunden">
                URL
              </th>
              <th style={{ textAlign: 'right' }}>Diensttage</th>
              <th style={{ textAlign: 'right' }}>Soll</th>
              <th style={{ textAlign: 'right' }}>Saldo</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.fahrerId}>
                <td>
                  <strong>{z.fahrerName}</strong>
                </td>
                <td style={{ color: 'var(--grau-500)' }}>{z.personalnummer ?? '—'}</td>
                <td style={{ color: 'var(--grau-500)' }}>{z.teamName ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>{alsDauer(z.arbeitszeit)}</td>
                <td style={{ textAlign: 'right' }}>{z.geteilteDienste || '—'}</td>
                <td style={{ textAlign: 'right' }}>{z.nachtminuten ? alsDauer(z.nachtminuten) : '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {z.sonntagsminuten ? alsDauer(z.sonntagsminuten) : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {z.feiertagsminuten ? alsDauer(z.feiertagsminuten) : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>{z.krankheitstage || '—'}</td>
                <td style={{ textAlign: 'right' }}>{z.urlaubstage || '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {z.urlaubsminuten ? alsDauer(z.urlaubsminuten) : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>{z.diensttage}</td>
                <td style={{ textAlign: 'right' }}>{alsDauer(z.sollminuten)}</td>
                <td
                  style={{
                    textAlign: 'right',
                    color:
                      z.saldo === null
                        ? undefined
                        : z.saldo < 0
                          ? 'var(--rot-600, #dc2626)'
                          : 'var(--gruen-600, #16a34a)',
                  }}
                >
                  {z.saldo === null ? '—' : alsSaldo(z.saldo)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
